// The § numbering rule and the lock-conflict predicate. A § is a heading plus everything
// under it, and its address is its outline number ("3.2.4") — the string the member reads
// off the screen and hands to a console agent.
//
// Numbering is deliberately factored out of parsing: a number depends on exactly two things,
// the ordered sequence of heading LEVELS and the part's `startRoot` offset. Those are all the
// web and the backend need to agree on, and neither parses the other's input — the web derives
// levels from the live ProseMirror doc (components/editor/pm-sections.ts), the backend scans
// markdown, and the offset travels over HTTP.
//
// The run is ARTEFACT-wide: a part continues the numbering of the parts before it rather than
// restarting at 1, so part 2 opens at §3 when part 1 held two top-level §.
//
// Parity: backend/domain/sections.ts holds an identical copy of both functions (the web app
// cannot import backend source). Keep the two in sync — the shared fixture corpus, duplicated
// into backend/domain/sections.test.ts and components/editor/SectionNumbers.test.ts, is what
// proves the two sides really do produce the same levels.

/** THE numbering rule. Tree-relative (a stack that pops on level <=), never level-literal, so
 *  a part starting at `##` still numbers its first heading as a root of the run and a level
 *  skip never emits "1.0.1".
 *
 *  `startRoot` is the part's OFFSET — how many top-level § the parts before it in the artefact
 *  already used, so numbering continues across parts instead of restarting at 1. It seeds the
 *  root counter only; children hang off their parent's frame and never shift. The backend hands
 *  this number out (GET /v1/sections → `offset`); the web never computes it, since it depends on
 *  sibling rooms this client is not connected to.
 *
 *  The child counter lives on the stack FRAME, not in a depth-indexed array: a counter[depth]
 *  implementation numbers `# A / ## A1 / # B / ## B1` as 2.2 instead of 2.1. */
export function numberLevels(levels: number[], startRoot = 0): string[] {
  interface Frame {
    level: number
    number: string
    children: number
  }
  const stack: Frame[] = []
  let roots = startRoot
  return levels.map((level) => {
    while (stack.length && stack[stack.length - 1]!.level >= level) stack.pop()
    const parent = stack[stack.length - 1]
    const n = parent ? ++parent.children : ++roots
    const number = parent ? `${parent.number}.${n}` : String(n)
    stack.push({ level, number, children: 0 })
    return number
  })
}

/** '' = whole part. Ancestor/descendant containment — the lock conflict predicate.
 *  The trailing dot avoids the prefix trap: '3.2' must not conflict with '3.20'. */
export function sectionsConflict(a: string, b: string): boolean {
  if (a === '' || b === '') return true
  return a === b || a.startsWith(b + '.') || b.startsWith(a + '.')
}

/** A § located in MARKDOWN coordinates — offsets into the Y.Text, not ProseMirror positions. */
export interface MarkdownSection {
  number: string
  /** UTF-16 offset of the heading LINE's first char. Inclusive. */
  start: number
  /** Start of the next heading of level <= this one, else markdown.length. Exclusive.
   *  INCLUDES descendants — the subtree IS the section. */
  end: number
}

// 0-3 leading spaces, then #{1,6}, then a space/tab or end-of-line. The "first non-space char
// must be #" rule means `> ## quoted` and `- ## listed` are NOT headings.
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+[^\r\n]*?)?[ \t]*\r?$/
/** Fence opener/closer: 3+ backticks or tildes, 0-3 leading spaces. */
const FENCE = /^ {0,3}(`{3,}|~{3,})/

/**
 * Locate every § in a part's markdown.
 *
 * Parity: backend/domain/sections.ts#parseSections is the same scanner over the same input —
 * this one only drops the title/level fields, which the lock guard has no use for. It runs on the
 * Y.Text, i.e. on exactly the bytes the backend scanned when it keyed the lock, so the numbers
 * here and the numbers in a Revision.section cannot disagree. (SectionNumbers.ts derives numbers
 * from the ProseMirror doc instead — right for rendering, wrong here: PM positions are a
 * different coordinate system from Y.Text offsets, and conflating the two is what corrupts text.)
 */
export function parseSections(markdown: string, startRoot = 0): MarkdownSection[] {
  const heads: { level: number; start: number }[] = []
  let fence: string | null = null

  for (let i = 0; i < markdown.length; ) {
    const nl = markdown.indexOf('\n', i)
    const lineEnd = nl === -1 ? markdown.length : nl
    const line = markdown.slice(i, lineEnd)

    const fenceMatch = FENCE.exec(line)
    if (fence !== null) {
      // A closing fence uses the same char and is at least as long as the opener.
      if (fenceMatch && fenceMatch[1]![0] === fence[0] && fenceMatch[1]!.length >= fence.length) {
        fence = null
      }
    } else if (fenceMatch) {
      fence = fenceMatch[1]!
    } else {
      const m = HEADING.exec(line)
      if (m) heads.push({ level: m[1]!.length, start: i })
    }

    i = nl === -1 ? markdown.length : nl + 1
  }

  const numbers = numberLevels(
    heads.map((h) => h.level),
    startRoot,
  )
  return heads.map((h, idx) => {
    let end = markdown.length
    for (let j = idx + 1; j < heads.length; j++) {
      if (heads[j]!.level <= h.level) {
        end = heads[j]!.start
        break
      }
    }
    return { number: numbers[idx]!, start: h.start, end }
  })
}
